import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  setCurrentStep,
  setResetInterviewSlice
} from '../../redux/Slices/InterviewSlice';
import { Language } from '../../redux/Slices/InterviewSlice/types';
import {
  resetCompleteState,
  setIsStartTest
} from '../../redux/Slices/QuestionsSlice';
import {
  resetTestSettings,
  setCurrentSectionIndex,
  setCurrentTime,
  setResetSatrtFullTest
} from '../../redux/Slices/StartTestSlice/startFulTest';
import { useAppDispatch, useAppSelector } from '../../redux/store/hooks';
import {
  useGetFileFromS3Mutation,
  useUploadFileToS3Mutation
} from '../../services/CloudApi';
import {
  useDriveEndTestMutation,
  useDriveSubmitTestMutation
} from '../../services/driveApi';
import { useHrSubmitAnswerMutation } from '../../services/hrApi';
import {
  useGetAvatarsQuery,
  useGetTopicQuery
} from '../../services/interViewApi';
import Loader from '../Common/Loader';
import InterviewInstructionsV2 from '../ReUsableComponents/InterviewInstructions/InterviewInstructionsV2';

import HrDashboard from './HrDashboard';
import Interview from './Interview';
import InterviewComplete from './InterviewComplete';

const HRRound = () => {
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(
    null
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [overallTimer, setOverallTimer] = useState(720);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [getFileFromS3] = useGetFileFromS3Mutation();
  const [uploadFileToS3] = useUploadFileToS3Mutation();
  const [hrSubmitAnswer] = useHrSubmitAnswerMutation();
  const [answersApiStatus, setAnswerApiStatus] = useState({});
  const [isLastAnswerSubmited, setIsLastAnswerSubmited] = useState(false);
  const { driveData } = useAppSelector(state => state.root.interview);
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  useGetTopicQuery({});
  useGetAvatarsQuery({}, { refetchOnMountOrArgChange: true });
  const [submitDriveTest] = useDriveSubmitTestMutation();
  const [endDriveTest] = useDriveEndTestMutation();

  const { testData, currentStep } = useAppSelector(
    state => state.root.interview
  );
  const { isDriveTest } = useAppSelector(state => state.root.startFulltest);

  const audioRef = useRef<HTMLAudioElement>(null);

  // Initialize media recorder
  useEffect(() => {
    const initMediaRecorder = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        const recorder = new MediaRecorder(stream);

        recorder.ondataavailable = event => {
          if (event.data.size > 0) {
            const audioBlob = new Blob([event.data], {
              type: 'audio/webm'
            });
            const audioFile = new File([audioBlob], 'recording.webm', {
              type: 'audio/webm'
            });
            uploadToS3(audioFile);
            setRecordedChunks(prev => [...prev, event.data]);
          } else {
            console.warn('No audio data captured');
          }
        };

        setMediaRecorder(recorder);
      } catch (error) {
        console.error('Error accessing microphone:', error);
      }
    };

    if (currentStep === 'interview') {
      initMediaRecorder();
    }
  }, [currentStep, currentQuestionIndex]);

  const startRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state === 'inactive') {
      setRecordedChunks([]);
      mediaRecorder.start();
      setIsRecording(true);
    } else {
      console.warn('failed to start Recording', mediaRecorder);
    }
  }, [mediaRecorder]);

  const stopRecording = useCallback(() => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
    } else {
      console.warn('failed to Stop Recording', mediaRecorder);
    }
  }, [mediaRecorder]);

  const uploadToS3 = async (file: File) => {
    const questionid = testData?.testSet?.questions[currentQuestionIndex]?.id;
    try {
      const response = await uploadFileToS3(file).unwrap();
      const payload = {
        questionId: testData?.testSet?.questions[currentQuestionIndex]?.id,
        uploadedAnswerFile: {
          ...response.data,
          originalFileName: file.name
        }
      };
      setAnswerApiStatus(prev => ({
        ...prev,
        [payload.questionId]: 'pending'
      }));
      const res = await hrSubmitAnswer({
        userTestId: testData?.id,
        body: payload
      }).unwrap();
      if (res.status === 'success') {
        setAnswerApiStatus(prev => ({
          ...prev,
          [payload.questionId]: 'success'
        }));
      }
      if (
        currentQuestionIndex === testData?.testSet?.questions.length - 1 &&
        res.status === 'success'
      ) {
        // setIsLoading(false);
        // dispatch(setCurrentStep('complete'));
      }
    } catch (err) {
      setIsLoading(false);
      setAnswerApiStatus(prev => ({
        ...prev,
        [questionid]: 'error'
      }));
      console.error('Upload failed:', err);
    }
  };

  const playQuestion = useCallback(async () => {
    const currentQuestion = testData?.testSet?.questions[currentQuestionIndex];
    let url = '';
    if (currentQuestion?.audioFile?.fileName) {
      url = await getFileFromS3(currentQuestion?.audioFile?.fileName).unwrap();
    }
    if (audioRef.current && url) {
      setIsPlayingAudio(true);

      // Set up audio element
      audioRef.current.src = url;
      audioRef.current.load();

      // Handle audio end event
      audioRef.current.onended = () => {
        setIsPlayingAudio(false);
        setShowTimer(true);
        startRecording();
      };

      // Handle audio error
      audioRef.current.onerror = () => {
        setIsPlayingAudio(false);
        setShowTimer(true);
        startRecording();
      };

      try {
        await audioRef.current.play();
      } catch (error) {
        console.error('Error playing audio:', error);
        setIsPlayingAudio(false);
        setShowTimer(true);
        startRecording();
      }
    }
  }, [startRecording, currentQuestionIndex, testData]);

  const handleNextQuestion = useCallback(async () => {
    stopRecording();
    setShowTimer(false);
    const isLastQuestion =
      currentQuestionIndex >= testData?.testSet?.questions.length - 1;
    if (!isLastQuestion && overallTimer > 0) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setIsRecording(false);
      setIsPlayingAudio(false);
      setIsLoading(true);
      setIsLastAnswerSubmited(true);
      // setTimeout(() => {
      //   dispatch(setCurrentStep('complete'));
      // }, 4000);
    }
  }, [currentQuestionIndex, stopRecording, testData?.id, overallTimer]);

  const handleTimerComplete = useCallback(() => {
    handleNextQuestion();
  }, [handleNextQuestion]);

  const checkMicrophonePermission = async () => {
    try {
      // Check if navigator.mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('getUserMedia is not supported');
        window.alert(
          'Your browser does not support camera or microphone access. Please use a modern browser like Chrome, Firefox, or Edge.'
        );
        return;
      }
      // Try to get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      // If successful, we have permission
      dispatch(setCurrentStep('instructions'));
      stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      console.error('Microphone permission denied:', error);
      window.alert(
        'Microphone permission is required to start the test. Please allow microphone access in your browser settings.'
      );
    }
  };

  useEffect(() => {
    if (
      currentStep === 'interview' &&
      !showTimer &&
      !isPlayingAudio &&
      mediaRecorder
    ) {
      const timer = setTimeout(() => {
        playQuestion();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [
    currentQuestionIndex,
    currentStep,
    showTimer,
    isPlayingAudio,
    playQuestion,
    mediaRecorder
  ]);

  const endDriveTestFunction = async () => {
    try {
      const userTestId = driveData?.id;
      await submitDriveTest({
        userTestId,
        body: driveData
      }).unwrap();
      await endDriveTest(userTestId).unwrap();
      dispatch(setResetSatrtFullTest());
      dispatch(setResetInterviewSlice());
      dispatch(setCurrentStep('language'));
      dispatch(resetCompleteState());
      toast.success('Test submitted successfully');
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    const finalTestSubmission = async () => {
      const isLastQuestion =
        currentQuestionIndex >= testData?.testSet?.questions.length - 1;
      if (isLastAnswerSubmited && isLastQuestion) {
        const statuses = Object.values(answersApiStatus).every(
          each => each !== 'pending'
        );
        if (statuses) {
          isDriveTest && (await endDriveTestFunction());
          dispatch(setIsStartTest(false));
          !isDriveTest && dispatch(setCurrentStep('complete'));
          setIsLastAnswerSubmited(false);
          setAnswerApiStatus({});
          setIsLoading(false);
        }
      }
    };
    finalTestSubmission();
  }, [answersApiStatus]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentStep === 'interview') {
        const message = 'Changes you made may not be saved.';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStep]);

  useEffect(() => {
    const handleBackButton = (event: any) => {
      if (currentStep === 'interview') {
        const confirmBack = window.confirm(
          'Are you sure you want to leave this page? Any changes you have made will be lost, and youll need to start the test over from the beginning.'
        );
        if (!confirmBack) {
          event.preventDefault();
        } else {
          dispatch(setCurrentStep('start'));
          setCurrentQuestionIndex(0);
          setSelectedLanguage(null);
          setShowTimer(false);
          setIsRecording(false);
          setIsPlayingAudio(false);
          setOverallTimer(720);
        }
      }
    };

    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [location, navigate, currentStep]);

  useEffect(() => {
    if (!isDriveTest) {
      dispatch(setCurrentStep('start'));
    }
  }, [isDriveTest]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (currentStep === 'complete' && isDriveTest) {
      timer = setTimeout(() => {
        toast.success('Test submitted successfully');
        dispatch(setIsStartTest(false));
        dispatch(setCurrentSectionIndex(0));
        dispatch(resetCompleteState());
        dispatch(setResetSatrtFullTest());
        dispatch(setCurrentTime(0));
        dispatch(resetTestSettings());
      }, 5000); // 5 seconds
    }

    return () => clearTimeout(timer);
  }, [currentStep, isDriveTest]);

  if (currentStep === 'start') {
    return <HrDashboard />;
  }

  if (currentStep === 'instructions') {
    return <InterviewInstructionsV2 isHr={true} />;
  }

  if (currentStep === 'complete' && isDriveTest) {
    return (
      <Loader
        mainMessage='Analyzing your responses with our AI... please wait.'
        subMessage='Don’t perform any other actions or reload the page.'
      />
    );
  }

  if (currentStep === 'interview') {
    const currentQuestion =
      testData?.testSet?.questions?.[currentQuestionIndex];

    return (
      <Interview
        currentQuestionIndex={currentQuestionIndex}
        currentQuestion={currentQuestion}
        isPlayingAudio={isPlayingAudio}
        showTimer={showTimer}
        isRecording={isRecording}
        handleTimerComplete={handleTimerComplete}
        handleNextQuestion={handleNextQuestion}
        audioRef={audioRef}
        overallTimer={overallTimer}
        setOverallTimer={setOverallTimer}
        loading={isLoading}
      />
    );
  }

  const path = window.location.pathname;

  return (
    <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-green-50 to-blue-100 p-4'>
      <InterviewComplete
        setCurrentQuestionIndex={setCurrentQuestionIndex}
        setSelectedLanguage={setSelectedLanguage}
        setShowTimer={setShowTimer}
        setIsRecording={setIsRecording}
        setIsPlayingAudio={setIsPlayingAudio}
        setOverallTimer={setOverallTimer}
      />
    </div>
  );
};

export default HRRound;
